/*
 * geo — coarse geo-string → map position for fleet boxes. Real GeoIP coordinates
 * win; a known country code falls back to its centroid (+ a small fingerprint-
 * seeded jitter so same-country boxes don't perfectly stack); anything else gets a
 * deterministic hashed land-ish position, flagged `approximate` so a UI can say
 * "positions approximate (telemetry pending)". Shared by the fleet map page and
 * the dashboards map widget — one placement rule everywhere.
 */
// A compact lookup (the fleet is FR-heavy + US). Anything not here falls back to
// the deterministic hashed position so the map is never empty in dev.
const COUNTRY = {
    FR: { region: 'EMEA', lat: 46.6, lng: 2.4 },
    DE: { region: 'EMEA', lat: 51.2, lng: 10.5 },
    GB: { region: 'EMEA', lat: 54.0, lng: -2.0 },
    UK: { region: 'EMEA', lat: 54.0, lng: -2.0 },
    ES: { region: 'EMEA', lat: 40.4, lng: -3.7 },
    IT: { region: 'EMEA', lat: 42.8, lng: 12.6 },
    NL: { region: 'EMEA', lat: 52.1, lng: 5.3 },
    CH: { region: 'EMEA', lat: 46.8, lng: 8.2 },
    BE: { region: 'EMEA', lat: 50.6, lng: 4.6 },
    SE: { region: 'EMEA', lat: 62.0, lng: 15.0 },
    PL: { region: 'EMEA', lat: 52.0, lng: 19.1 },
    PT: { region: 'EMEA', lat: 39.6, lng: -8.0 },
    IE: { region: 'EMEA', lat: 53.4, lng: -8.2 },
    AT: { region: 'EMEA', lat: 47.6, lng: 14.1 },
    AE: { region: 'EMEA', lat: 24.0, lng: 54.0 },
    ZA: { region: 'EMEA', lat: -30.6, lng: 22.9 },
    US: { region: 'Americas', lat: 39.0, lng: -98.0 },
    CA: { region: 'Americas', lat: 56.1, lng: -106.3 },
    MX: { region: 'Americas', lat: 23.6, lng: -102.5 },
    BR: { region: 'Americas', lat: -14.2, lng: -51.9 },
    AR: { region: 'Americas', lat: -38.4, lng: -63.6 },
    CL: { region: 'Americas', lat: -35.7, lng: -71.5 },
    JP: { region: 'APAC', lat: 36.2, lng: 138.3 },
    CN: { region: 'APAC', lat: 35.9, lng: 104.2 },
    KR: { region: 'APAC', lat: 36.5, lng: 127.9 },
    IN: { region: 'APAC', lat: 21.1, lng: 78.0 },
    AU: { region: 'APAC', lat: -25.3, lng: 133.8 },
    NZ: { region: 'APAC', lat: -41.0, lng: 174.0 },
    SG: { region: 'APAC', lat: 1.35, lng: 103.8 },
    TW: { region: 'APAC', lat: 23.7, lng: 121.0 },
};
/** Extract a 2-letter country code from a coarse geo string ("Paris, FR" → "FR"). */
function countryCode(geo) {
    const m = geo.toUpperCase().match(/\b([A-Z]{2})\b/);
    return m && COUNTRY[m[1]] ? m[1] : null;
}
export function regionOf(geo) {
    const cc = countryCode(geo);
    return cc ? COUNTRY[cc].region : 'Unknown';
}
/** Small stable hash of a string → unsigned 32-bit (FNV-1a). */
function hash32(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * Map a gateway to a map position. Real GeoIP coordinates win; otherwise a known
 * country's centroid + fingerprint-seeded jitter; otherwise a deterministic hashed
 * land-ish position, flagged approximate.
 */
export function geoToLatLng(g) {
    if (g.lat !== 0 || g.lng !== 0) {
        return { lat: g.lat, lng: g.lng, approximate: false };
    }
    const cc = countryCode(g.geo);
    const h = hash32(g.deviceFingerprint || g.geo || 'x');
    const jLat = ((h & 0xff) / 255 - 0.5) * 6; // ±3°
    const jLng = (((h >>> 8) & 0xff) / 255 - 0.5) * 6;
    if (cc) {
        const e = COUNTRY[cc];
        return { lat: e.lat + jLat, lng: e.lng + jLng, approximate: false };
    }
    // No usable geo → spread deterministically across a land-ish band.
    const lat = ((h & 0xffff) / 0xffff) * 100 - 35; // -35..65
    const lng = (((h >>> 16) & 0xffff) / 0xffff) * 280 - 130; // -130..150
    return { lat, lng, approximate: true };
}
//# sourceMappingURL=geo.js.map